"use client";

import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { OffersService } from "@/services/OffersService";
import type { OffersOutput } from "@/interfaces/offers";
import { numberToMoney } from "@/util/moneyConversion";
import { Plus, Tag } from "lucide-react";

interface SectionOfferSelectorProps {
  onSelect: (offer: OffersOutput) => void;
  placeholder?: string;
  selectedOfferIds?: string[];
}

export function SectionOfferSelector({
  onSelect,
  placeholder = "Adicionar Oferta",
  selectedOfferIds = [],
}: SectionOfferSelectorProps) {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [offers, setOffers] = useState<OffersOutput[]>([]);
  const [loading, setLoading] = useState(false);

  const offersService = useMemo(() => new OffersService(), []);

  useEffect(() => {
    // Só carregar ofertas quando o popover estiver aberto
    if (!open) return;

    const loadOffers = async () => {
      setLoading(true);
      try {
        const response = await offersService.findAll({
          page: 1,
          pageSize: 100,
          query: searchValue,
        });
        setOffers(response.data);
      } finally {
        setLoading(false);
      }
    };

    loadOffers();
  }, [open, searchValue, offersService]);

  const handleSelectOffer = (offer: OffersOutput) => {
    onSelect(offer);
    setOpen(false);
    setSearchValue("");
  };

  // Filtrar ofertas já selecionadas
  const availableOffers = offers.filter(
    (offer) => !selectedOfferIds.includes(offer.id)
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="justify-between h-9 px-3"
          size="sm"
        >
          <div className="flex items-center gap-2">
            <Tag className="h-4 w-4 text-orange-500" />
            <span className="text-sm">{placeholder}</span>
          </div>
          <Plus className="h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <Command>
          <CommandInput
            placeholder="Buscar ofertas..."
            value={searchValue}
            onValueChange={setSearchValue}
          />
          <CommandList>
            <CommandEmpty>
              {loading ? "Carregando..." : "Nenhuma oferta encontrada."}
            </CommandEmpty>
            <CommandGroup>
              {availableOffers.map((offer) => (
                <CommandItem
                  key={offer.id}
                  onSelect={() => handleSelectOffer(offer)}
                  className="flex items-center justify-between p-3 cursor-pointer"
                >
                  <div className="flex items-center gap-3 flex-1">
                    <div className="w-10 h-10 rounded-md overflow-hidden bg-gray-100 flex items-center justify-center">
                      <Tag className="h-5 w-5 text-orange-500" />
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-sm">{offer.name}</div>
                      <div className="text-xs text-gray-500">
                        Preço: {numberToMoney(offer.unitPrice)}
                        {offer.unitCost && (
                          <> | Custo: {numberToMoney(offer.unitCost)}</>
                        )}
                      </div>
                    </div>
                  </div>
                  <Plus className="h-4 w-4 text-orange-500" />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
